import React, { useState } from 'react';
import { IComment } from '../../../models';
import { Link, useNavigate } from 'react-router-dom';
import Avatar from '../../avatar/avatar.component';
import Nametag from '../../cosmetics/nametag.component';
import classNames from 'classnames';
import { HiOutlineDotsVertical, HiThumbDown, HiThumbUp } from 'react-icons/hi';
import dayjs from 'dayjs';
import { postService } from '../../../services/post.service';
import { useReplyComment } from '../../../hooks/post/useReplyComment';
import CommentReplyInputComponent from './comment-reply-input.component';
import { toast } from 'react-hot-toast';
import CommentMenu from './comment-menu.component';
import { useSelector } from 'react-redux';
import { RootState } from '../../../configuration';
import CommentVotersDialog from '../../comment-voters-dialog/comment-voters-dialog.component';
import VerifyBadge from '../../verify-badge/verify-badge.component';
import MemberMark from '../../membership/member-mark';
import { isEmojiOnly } from '../../../utils/emoji';

interface SingleCommentComponentProps {
  comment: IComment;
  refreshCommentClient?: () => void;
  updateComment?: (commentId: string, newContent: string) => void;
  deleteComment?: (commentId: string, commentType: number) => Promise<boolean>;
  updateCommentLike?: (commentId: string, type: number) => void;
  refreshCurrentPage?: () => Promise<void>;
  /** Id tác giả bài viết: huy hiệu hội viên tính theo kênh này. */
  postOwnerId?: string;
}

const SingleCommentComponent = ({
  comment,
  refreshCommentClient,
  updateComment,
  deleteComment,
  updateCommentLike,
  refreshCurrentPage,
  postOwnerId,
}: SingleCommentComponentProps) => {
  const navigate = useNavigate();
  const userProfile = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const [replyCommentId, setReplyCommentId] = useState<string>('');
  const [isReplyComment, setIsReplyComment] = useState<boolean>(false);
  const [isShowReply, setIsShowReply] = useState<boolean>(false);
  const [pageNumberReplyComment, setPageNumberReplyComment] =
    useState<number>(1);

  const { id, commentType } = comment;

  const {
    data: replyCommentList,
    hasMore: hasMoreReplyComment,
    loading: loadingReplyComment,
    getReplyCommentWhenReply,
    getReplyComment,
  } = useReplyComment({
    pageNumber: pageNumberReplyComment,
    commentId: replyCommentId,
    visible: isShowReply,
  });

  const openReply = (id: string) => {
    if (!userProfile) navigate('/account/login');
    if (!replyCommentId) {
      setIsReplyComment(true);
    }

    if (id === replyCommentId) {
      setIsReplyComment((prev) => !prev);
    } else {
      setIsReplyComment(true);
    }

    setReplyCommentId(id);
  };

  const isShowReplyList = isShowReply && id === replyCommentId;
  const isShowReplyInput = isReplyComment && id === replyCommentId;

  return (
    <div
      className={classNames(
        `flex items-start ${
          isShowReplyInput || isShowReplyList
            ? 'border-l-2 border-b-2 rounded-l-lg rounded-b-lg border-slate-50'
            : ''
        }`
      )}
    >
      <div className="w-full z-10">
        <CommmentComponent
          {...comment}
          openReply={openReply}
          setIsShowReply={setIsShowReply}
          isShowReply={isShowReply}
          setReplyCommentId={setReplyCommentId}
          replyCommentId={replyCommentId}
          refreshCommentClient={refreshCommentClient}
          updateComment={updateComment}
          deleteComment={deleteComment}
          updateCommentLike={updateCommentLike}
          refreshCurrentPage={refreshCurrentPage}
          postOwnerId={postOwnerId}
        />
        {isShowReplyList ? (
          <div className="space-y-2 ml-16">
            {replyCommentList?.map((reply, index) => (
              <CommmentComponent
                key={reply.id ?? index}
                {...reply}
                postOwnerId={postOwnerId}
              />
            ))}
            {hasMoreReplyComment ? (
              <button
                type="button"
                disabled={loadingReplyComment}
                onClick={() =>
                  getReplyComment({
                    pageNumber: pageNumberReplyComment,
                    pageSize: 5,
                    pagingState: '',
                    commentId: id,
                  })
                }
                className="mb-2 text-xs font-medium text-slate-600 hover:underline disabled:opacity-60 dark:text-slate-300"
              >
                {loadingReplyComment ? 'Đang tải...' : 'Xem thêm trả lời'}
              </button>
            ) : null}
          </div>
        ) : null}
        {isShowReplyInput && (
          <CommentReplyInputComponent
            commentId={id}
            replyToName={comment?.userFullName || comment?.username || ''}
            getReplyCommentWhenReply={getReplyCommentWhenReply}
            setReplyCommentId={setReplyCommentId}
            isShowReply={isShowReply}
            setIsShowReply={setIsShowReply}
          />
        )}
      </div>
    </div>
  );
};

const CommmentComponent = (props: any) => {
  const {
    userId,
    avatar,
    userFullName,
    username,
    userType,
    content,
    createdAt,
    currentUserHasLike,
    currentUserHasUnlike,
    totalReply,
    id,
    openReply,
    setReplyCommentId,
    setIsShowReply,
    isShowReply,
    replyCommentId,
    refreshCommentClient,
    downvotesCount,
    upvotesCount,
    commentType,
    updateComment,
    deleteComment,
    updateCommentLike,
    refreshCurrentPage,
    postOwnerId,
  } = props;

  const [votersType, setVotersType] = useState<number | null>(null);

  const ToggleLike = async (id: string, type: number) => {
    try {
      if (currentUserHasLike || currentUserHasUnlike) {
        currentUserHasLike
          ? toast.error('Bạn đã thích bình luận này')
          : toast.error('Bạn đã không thích bình luận này');
        return;
      }
      await postService.likeComment({ commentId: id, type });
      // Update the UI immediately
      updateCommentLike(id, type);
      // Refresh with current page size
      await refreshCurrentPage();
    } catch (error) {
      toast.error('Có lỗi xảy ra');
    }
  };
  const profileUser = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  // Tài khoản đã xoá thì không có username: không dựng link (tránh /user/null), chỉ hiện tên và ảnh.
  const avatarEl = (
    <Avatar avatar={avatar} size="xs" label={userFullName} userId={userId} />
  );
  return (
    <div className="flex">
      {username ? (
        <Link to={`/user/${username}`} className="cursor-pointer">
          {avatarEl}
        </Link>
      ) : (
        avatarEl
      )}
      <div className="ml-4 flex-1 min-w-0 mb-2">
        {/* Bong bóng xám ôm sát nội dung (kiểu Facebook): bo tròn, nền sáng, chỉ rộng bằng nội dung; menu ⋯ ra ngoài */}
        <div className="flex items-center gap-1">
          <div className="w-fit max-w-full min-w-0 rounded-2xl">
            {userFullName ? (
              <Nametag userId={userId} banner compact>
                {username ? (
                  <Link
                    to={`/user/${username}`}
                    className="inline-block text-[13px] font-semibold leading-tight text-black dark:text-white"
                  >
                    {userFullName}
                  </Link>
                ) : (
                  <span className="inline-block text-[13px] font-semibold leading-tight text-black dark:text-white">
                    {userFullName}
                  </span>
                )}
                {userType === 'CONTENT_CREATOR' ? (
                  <VerifyBadge
                    userType={userType}
                    size={14}
                    className="inline-block ml-1 align-text-bottom"
                  />
                ) : null}
                <MemberMark creatorId={postOwnerId} userId={userId} />
              </Nametag>
            ) : (
              <span className="text-[13px] font-semibold text-black dark:text-white">
                Người dùng không xác định
              </span>
            )}
            <p className={`mt-0.5 break-words whitespace-pre-wrap ${isEmojiOnly(content) ? 'text-[30px] leading-tight' : 'text-[15px]'}`}>{content}</p>
          </div>
          <CommentMenu
            commentId={id}
            userId={profileUser?.userId ?? ''}
            commentOwnerId={userId}
            onDelete={refreshCommentClient}
            onUpdate={updateComment}
            commentType={commentType}
            content={content}
            updateComment={updateComment}
            deleteComment={deleteComment}
          />
        </div>
        <div className="flex items-center justify-star mt-1">
          <div className="flex justify-end items-center text-slate-800 dark:text-slate-50">
            <span className="text-xs ">
              {dayjs.utc(createdAt).local().fromNow()}
            </span>
            <div
              className={`ml-2 flex items-center text-sm font-medium ${
                currentUserHasLike ? 'text-blue-500' : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              <button
                type="button"
                aria-label="Thích"
                onClick={() => ToggleLike(id, 1)}
                className="grid h-8 w-8 place-items-center rounded-full transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <HiThumbUp size={18} />
              </button>
              <button
                type="button"
                title="Xem người đã thích"
                onClick={(e) => {
                  e.stopPropagation();
                  if (upvotesCount > 0) setVotersType(1);
                }}
                className="min-w-[28px] rounded-full px-2 py-1 text-center transition-colors hover:bg-slate-100 hover:underline dark:hover:bg-slate-700"
              >
                {upvotesCount}
              </button>
            </div>

            <div
              className={`ml-1 flex items-center text-sm font-medium ${
                !currentUserHasUnlike ? 'text-slate-500 dark:text-slate-400' : 'text-red-500'
              }`}
            >
              <button
                type="button"
                aria-label="Không thích"
                onClick={() => ToggleLike(id, -1)}
                className="grid h-8 w-8 place-items-center rounded-full transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <HiThumbDown size={18} />
              </button>
              <button
                type="button"
                title="Xem người không thích"
                onClick={(e) => {
                  e.stopPropagation();
                  if (downvotesCount > 0) setVotersType(-1);
                }}
                className="min-w-[28px] rounded-full px-2 py-1 text-center transition-colors hover:bg-slate-100 hover:underline dark:hover:bg-slate-700"
              >
                {downvotesCount}
              </button>
            </div>
            {!profileUser?.isBlockComment && openReply && (
              <p
                className="cursor-pointer text-xs ml-3 font-medium"
                onClick={() => openReply(id)}
              >
                Phản hồi
              </p>
            )}
            {totalReply ? (
              <p
                className="cursor-pointer text-xs ml-3  text-slate-800 dark:text-slate-50 font-medium"
                onClick={() => {
                  setIsShowReply((prev) => !prev);
                  setReplyCommentId(id);
                }}
              >
                {isShowReply && id === replyCommentId
                  ? 'Thu gọn'
                  : `Xem tất cả ${totalReply} phản hồi`}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      {votersType !== null && (
        <CommentVotersDialog
          commentId={id}
          type={votersType}
          onClose={() => setVotersType(null)}
        />
      )}
    </div>
  );
};

export default SingleCommentComponent;
